import { IGenericPayload } from "../../../../../../../models";
import { useAppDispatch, useAppSelector } from "../../../../../../../redux/hooks";
import { compileNameOfProperty, getGenericPayloadFromEvent, mapIDescriptionToDropDownOptions } from "../../../../../../../utils";
import React, { useCallback, useMemo } from "react";
import { IDropdownOption } from "@fluentui/react";
import { NotDataWarningMessage } from "../../../../notDataWarning/notDataWarning";
import { Selector } from "../../../../../../common/selectors/selector";
import { useTranslation } from "react-i18next";
import { useTheme } from "react-jss";
import { MasterDocksFormStyle } from "./formStyle.jss";
import { FormField } from "../../../../../../common/fields/fields";

import { BooleanSelector } from "../../../../../../request/common/selectors";
import { useForm } from "react-hook-form";


import { DefaultValues } from "../../../../../../../common/enum/DefaultValues";
import { PopUpCodes } from "../../../../../../../common/enum/PopUpCodes";
import { IMasterDocumentTypeRemaining, IMasterDocumentType } from "../../../../../../../models/resources/master/IDocumentType";
import { updateMasterDocumentTypesProperty, setMasterDocumentTypesForm } from "../../../../../../../redux/reducers/resources/master/documentTypes/form";

interface IMasterDocumentTypesFormProps {
    showNotData: boolean;
    selectorData: IMasterDocumentTypeRemaining[];
}

export const MasterDocumentTypesForm: React.FC<IMasterDocumentTypesFormProps> = ({ selectorData, showNotData }) => {
    const { t } = useTranslation("common", { keyPrefix: "MASTERS" });
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const style = MasterDocksFormStyle({ theme });
    const { register, control } = useForm<IMasterDocumentType>({ mode: "onChange" });
    const { showPopUp } = useAppSelector((state) => state.masterPopUp);
    const { form } = useAppSelector((state) => state.masterDocumentTypesForm);

    const onUpdateFormProperty = (name: string, value: any) => {
        const payload: IGenericPayload = {
            name,
            value,
        };
        dispatch(updateMasterDocumentTypesProperty(payload));
    };
    const onInputChange = useCallback(
        (event: any) => {
            const { name, value } = getGenericPayloadFromEvent(event);
            onUpdateFormProperty(name, value);
        },
        [dispatch],
    );
    const onYesNoOptionsChange = (option: IDropdownOption, propertyName: string) => {
        onUpdateFormProperty(propertyName, !!+option.key);
    };
    const onSelectorChange = useCallback(
        (event: any, option: any, name: string) => {
            onUpdateFormProperty(name, option.key);
            let document = selectorData.find((item) => item.id === option.key);
            if (document) {
                let form: IMasterDocumentType = {
                    active: true,
                    code: document.code,
                    description: document.description,
                    referenceId: String(document.id),
                };
                dispatch(setMasterDocumentTypesForm(form));
            }
        },
        [dispatch, selectorData],
    );

    const disabledInput = useMemo(() => {
        return showPopUp === PopUpCodes.CREATE && !form.referenceId;
    }, [showPopUp, form.referenceId]);
    return showNotData && showPopUp === PopUpCodes.CREATE ? (
        <NotDataWarningMessage></NotDataWarningMessage>
    ) : (
        <div className={style.form}>
            {!form.id && (
                <div className={style.rowForm}>
                    <Selector
                        selectedKey={Number(form.referenceId)}
                        name={compileNameOfProperty<IMasterDocumentType>("referenceId")}
                        title={t("FORM.DOCUMENT_SELECTOR")}
                        options={mapIDescriptionToDropDownOptions(selectorData)}
                        onChange={onSelectorChange}
                        required={true}
                    />
                </div>
            )}

            <div className={style.rowForm}>
                <FormField
                    type="text"
                    label={t("CODE")}
                    value={form.code ?? DefaultValues.NOT_TEXT}
                    disabled={true}
                    {...register(compileNameOfProperty<IMasterDocumentType>("code"), {
                        onChange: onInputChange,
                        value: form.code ?? DefaultValues.NOT_TEXT,
                    })}
                    isRequired={true}
                />

                <FormField
                    type="text"
                    label={t("DESCRIPTION")}
                    value={form.description ?? DefaultValues.NOT_TEXT}
                    {...register(compileNameOfProperty<IMasterDocumentType>("description"), {
                        onChange: onInputChange,
                        value: form.description ?? DefaultValues.NOT_TEXT,
                    })}
                    isRequired={true}
                    disabled={disabledInput}
                />
            </div>
            <div className={style.rowForm}>
                <BooleanSelector
                    onChange={onYesNoOptionsChange}
                    selectedKey={+form.active}
                    propertyName={compileNameOfProperty<IMasterDocumentType>("active")}
                    title={t("ACTIVE")}
                    control={control}
                    isRequired={true}
                    disabled={disabledInput}
                />
            </div>
        </div>
    );
};
